import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { getHymns, getCategories, addHymn, updateHymn, deleteHymn, addCategory, updateCategory, deleteCategory, uploadIcon } from "../firebase";

const COLORS = ["#29ABE2","#F7941D","#1B75BB","#0B5FA5","#C84B7C","#7C5CBF","#2E7D9B","#2D8F5E","#DC2626","#059669","#D97706","#4338CA"];

// مجموعة رموز جاهزة للأقسام والتماجيد (تعمل مجانًا بدون رفع صور)
const ICON_SET = ["☩","✝️","✟","♰","🕊️","👑","⭐","✨","🌟","💫","🌹","🌸","🌷","🌺","🕯️","📿","📖","📜","🎵","🎶","🎼","🙏","😇","👼","⛪","🏛️","🌿","🍃","🔥","💧","🌅","💙","💛","❤️","🤍","☀️","🌙","⚜️"];

// يقرأ صورة من الجهاز، يصغّرها، ثم يعيدها كـ data URL (base64) لتُحفظ داخل قاعدة البيانات
function fileToResizedDataURL(file, max = 128) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w >= h && w > max) { h = Math.round((h * max) / w); w = max; }
        else if (h > w && h > max) { w = Math.round((w * max) / h); h = max; }
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [section, setSection] = useState("hymns");
  const [hymns, setHymns] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingHymn, setEditingHymn] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [toast, setToast] = useState(null);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };
  const gc = (id) => cats.find((c) => c.id === id) || { nameAr: "—", icon: "☩", color: "#ccc" };

  // يعرض أيقونة القسم: صورة مرفوعة (data URL) إن وُجدت، وإلا الإيموجي
  const isImg = (v) => typeof v === "string" && v.startsWith("data:");
  const catIconNode = (c, fontSize = 26) => isImg(c.image)
    ? <img src={c.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
    : <span style={{ fontSize: Math.round(fontSize * 1.15), lineHeight: 1 }}>{c.icon}</span>;
  const handleIconFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await fileToResizedDataURL(file, 128);
      setEditingCat((prev) => ({ ...prev, image: dataUrl, iconType: "image" }));
    } catch (err) {
      flash("❌ خطأ في قراءة الصورة");
    }
    e.target.value = "";
  };
  // أيقونة التمجيد: صورة من الجهاز تُحفظ (data URL) في حقل icon مباشرةً (مثل صورة القسم)
  const handleHymnIconFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await fileToResizedDataURL(file, 128);
      setEditingHymn((prev) => ({ ...prev, icon: dataUrl }));
    } catch (err) {
      flash("❌ خطأ في قراءة الصورة");
    }
    e.target.value = "";
  };

  // Load data
  useEffect(() => {
    async function load() {
      try {
        const [h, c] = await Promise.all([getHymns(), getCategories()]);
        setHymns(h);
        setCats(c);
      } catch (err) {
        console.error("Load error:", err);
      }
      setLoading(false);
    }
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = hymns;
    if (filterCat !== "all") list = list.filter((h) => h.categoryId === filterCat);
    if (search) { const q = search.toLowerCase(); list = list.filter((h) => h.titleAr?.includes(q) || h.titleEn?.toLowerCase().includes(q)); }
    return list;
  }, [hymns, filterCat, search]);

  const sortedCats = useMemo(() => [...cats].sort((a, b) => (a.order || 0) - (b.order || 0)), [cats]);

  // هل التمجيد الجاري تحريره ضمن قسم «مقدمة/ختام»؟ (تظهر عندها أعمدة القبطي واسم اللحن)
  const editingHymnCat = editingHymn ? cats.find((c) => c.id === editingHymn.categoryId) : null;
  const isLiturgicalHymn = /مقدمة|ختام/.test((editingHymnCat && editingHymnCat.nameAr) || "");

  // ━━━━ Hymn CRUD ━━━━

  const openNewHymn = () => {
    const maxSN = Math.max(0, ...hymns.map((h) => h.serialNumber || 0));
    setEditingHymn({
      serialNumber: maxSN + 1, titleAr: "", titleEn: "", icon: "🎵",
      melodyAr: "", melodyEn: "", tuneName: "", occasionAr: "", occasionEn: "",
      categoryId: cats[0]?.id || "general", youtubeUrl: "",
      status: "draft", isFeatured: false, order: 1,
      verses: [{ verseNumber: 1, arRight: "", arLeft: "", enRight: "", enLeft: "", coptic: "", copticArabic: "" }],
      _isNew: true
    });
  };

  const saveHymn = async () => {
    if (!editingHymn.titleAr) return flash("⚠️ العنوان بالعربي مطلوب");
    try {
      const { _isNew, id, ...data } = editingHymn;
      if (_isNew) {
        const newId = await addHymn(data);
        setHymns((prev) => [...prev, { id: newId, ...data }]);
      } else {
        await updateHymn(id, data);
        setHymns((prev) => prev.map((h) => (h.id === id ? { id, ...data } : h)));
      }
      setEditingHymn(null);
      flash("✅ تم الحفظ بنجاح");
    } catch (err) {
      flash("❌ خطأ: " + err.message);
    }
  };

  const removeHymn = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا التمجيد؟")) return;
    try {
      await deleteHymn(id);
      setHymns((prev) => prev.filter((h) => h.id !== id));
      setEditingHymn(null);
      flash("🗑 تم الحذف");
    } catch (err) {
      flash("❌ خطأ: " + err.message);
    }
  };

  // Verse management
  const addVerse = () => {
    const max = Math.max(0, ...editingHymn.verses.map((v) => v.verseNumber));
    setEditingHymn({ ...editingHymn, verses: [...editingHymn.verses, { verseNumber: max + 1, arRight: "", arLeft: "", enRight: "", enLeft: "", coptic: "", copticArabic: "" }] });
  };
  const updateVerse = (i, field, val) => {
    const v = [...editingHymn.verses]; v[i] = { ...v[i], [field]: val }; setEditingHymn({ ...editingHymn, verses: v });
  };
  const removeVerse = (i) => {
    if (editingHymn.verses.length <= 1) return;
    setEditingHymn({ ...editingHymn, verses: editingHymn.verses.filter((_, j) => j !== i).map((v, j) => ({ ...v, verseNumber: j + 1 })) });
  };

  // ━━━━ Category CRUD ━━━━

  const openNewCat = () => {
    const maxOrder = Math.max(0, ...cats.map((c) => c.order || 0));
    setEditingCat({ id: "", nameAr: "", nameEn: "", icon: "☩", iconType: "emoji", color: "#29ABE2", order: maxOrder + 1, parentId: "", _isNew: true });
  };

  const saveCat = async () => {
    if (!editingCat.nameAr || !editingCat.nameEn) return flash("⚠️ الاسم مطلوب بالعربي والإنجليزي");
    try {
      const { _isNew, ...data } = editingCat;
      if (_isNew) {
        const id = data.id || data.nameEn.toLowerCase().replace(/[^a-z0-9]/g, "_");
        await addCategory({ ...data, id });
        setCats((prev) => [...prev, { ...data, id }]);
      } else {
        await updateCategory(data.id, data);
        setCats((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      }
      setEditingCat(null);
      flash("✅ تم الحفظ");
    } catch (err) {
      flash("❌ خطأ: " + err.message);
    }
  };

  const removeCat = async (id) => {
    const count = hymns.filter((h) => h.categoryId === id).length;
    if (count > 0) return flash("⚠️ لا يمكن حذف قسم يحتوي على تماجيد (" + count + ")");
    if (!window.confirm("هل أنت متأكد من حذف هذا القسم؟")) return;
    try {
      await deleteCategory(id);
      setCats((prev) => prev.filter((c) => c.id !== id));
      flash("🗑 تم الحذف");
    } catch (err) {
      flash("❌ خطأ: " + err.message);
    }
  };

  // ━━━━ STYLES ━━━━
  const P = { bg: "#F0F7FC", s1: "#FFFFFF", tx: "#0B3D5C", tx2: "#5E8299", pr: "#29ABE2", ac: "#F7941D", bd: "#CBE2F0", ok: "#16A34A", dn: "#DC2626" };
  const inp = { width: "100%", padding: "11px 14px", borderRadius: 10, border: `1.5px solid ${P.bd}`, background: P.bg, fontSize: 14, fontFamily: "'Noto Sans Arabic', sans-serif", outline: "none", boxSizing: "border-box", direction: "rtl", color: P.tx };
  const inpEn = { ...inp, direction: "ltr", textAlign: "left", fontFamily: "'Outfit', sans-serif" };
  const lbl = { fontSize: 12, fontWeight: 600, color: P.tx2, marginBottom: 6, display: "block" };

  if (loading) return <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: P.bg }}><div style={{ color: P.tx2 }}>جاري التحميل...</div></div>;

  return (
    <div style={{ display: "flex", height: "100vh", fontFamily: "'Noto Sans Arabic', sans-serif", direction: "rtl", background: P.bg, color: P.tx }}>
      {/* ━━━ SIDEBAR ━━━ */}
      <div style={{ width: 220, background: "linear-gradient(180deg, #063356, #0B5FA5)", color: "#fff", display: "flex", flexDirection: "column", flexShrink: 0 }}>
        <div style={{ padding: "24px 20px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>☩</div>
            <div><div style={{ fontSize: 16, fontWeight: 700 }}>تماجيد جديدة</div><div style={{ fontSize: 11, opacity: 0.7 }}>لوحة التحكم</div></div>
          </div>
        </div>
        <div style={{ padding: "16px 0", flex: 1 }}>
          {[
            { key: "hymns", icon: "🎵", label: "التماجيد" },
            { key: "cats", icon: "📂", label: "الأقسام" },
          ].map((nav) => (
            <button key={nav.key} onClick={() => { setSection(nav.key); setEditingHymn(null); }} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "12px 24px", background: section === nav.key ? "rgba(255,255,255,0.15)" : "transparent", color: section === nav.key ? "#F7941D" : "rgba(255,255,255,0.65)", border: "none", fontSize: 14, fontWeight: section === nav.key ? 600 : 400, cursor: "pointer", fontFamily: "inherit", textAlign: "right" }}>
              <span style={{ fontSize: 18 }}>{nav.icon}</span>{nav.label}
            </button>
          ))}
        </div>
        <div style={{ padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>{user?.email}</div>
          <button onClick={logout} style={{ padding: "8px 16px", borderRadius: 8, background: "rgba(255,255,255,0.1)", color: "#fff", border: "none", fontSize: 13, cursor: "pointer", fontFamily: "inherit", width: "100%" }}>تسجيل الخروج</button>
        </div>
      </div>

      {/* ━━━ MAIN CONTENT ━━━ */}
      <div style={{ flex: 1, overflow: "auto", padding: 28 }}>
        {/* HYMNS SECTION */}
        {section === "hymns" && !editingHymn && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: P.pr }}>🎵 إدارة التماجيد ({hymns.length})</h2>
              <button onClick={openNewHymn} style={{ padding: "10px 22px", borderRadius: 12, background: `linear-gradient(135deg, ${P.ac}, #E8882D)`, color: "#fff", border: "none", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>+ إضافة تمجيد</button>
            </div>
            {/* Filters */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
              <input style={{ ...inp, flex: "1 1 200px" }} placeholder="🔍 بحث..." value={search} onChange={(e) => setSearch(e.target.value)} />
              {sortedCats.map((c) => (
                <button key={c.id} onClick={() => setFilterCat(filterCat === c.id ? "all" : c.id)} style={{ padding: "7px 14px", borderRadius: 10, border: `1.5px solid ${filterCat === c.id ? c.color : P.bd}`, background: filterCat === c.id ? `${c.color}15` : P.s1, color: filterCat === c.id ? c.color : P.tx2, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>{c.icon} {c.nameAr?.replace("تماجيد ", "")}</button>
              ))}
            </div>
            {/* Table */}
            <div style={{ background: P.s1, borderRadius: 16, border: `1px solid ${P.bd}`, overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr>{["#", "التمجيد", "الوزن", "الفئة", "أبيات", "الحالة", "إجراءات"].map((h, i) => (
                    <th key={i} style={{ padding: "12px 14px", textAlign: "right", fontSize: 12, fontWeight: 600, color: P.tx2, background: "#E8F2FA", borderBottom: `2px solid ${P.bd}` }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {filtered.map((h) => {
                    const c = gc(h.categoryId);
                    return (
                      <tr key={h.id} style={{ cursor: "pointer" }} onDoubleClick={() => setEditingHymn({ ...h, verses: h.verses?.map((v) => ({ ...v })) || [] })}>
                        <td style={{ padding: "12px 14px", borderBottom: `1px solid ${P.bd}`, fontWeight: 700, color: P.tx2 }}>{h.serialNumber}</td>
                        <td style={{ padding: "12px 14px", borderBottom: `1px solid ${P.bd}` }}>
                          <div style={{ fontWeight: 600 }}><span style={{ marginInlineEnd: 6 }}>{isImg(h.icon) ? "🖼️" : (h.icon || "🎵")}</span>{h.isFeatured ? "⭐ " : ""}{h.titleAr}</div>
                          <div style={{ fontSize: 12, color: P.tx2, fontFamily: "'Outfit'" }}>{h.titleEn}</div>
                        </td>
                        <td style={{ padding: "12px 14px", borderBottom: `1px solid ${P.bd}`, fontSize: 12, color: P.tx2 }}>{h.melodyAr || "—"}</td>
                        <td style={{ padding: "12px 14px", borderBottom: `1px solid ${P.bd}` }}>
                          <span style={{ padding: "3px 10px", borderRadius: 8, background: `${c.color}15`, color: c.color, fontSize: 12, fontWeight: 600 }}>{c.icon} {c.nameAr?.replace("تماجيد ", "")}</span>
                        </td>
                        <td style={{ padding: "12px 14px", borderBottom: `1px solid ${P.bd}`, textAlign: "center", fontWeight: 700 }}>{h.verses?.length || 0}</td>
                        <td style={{ padding: "12px 14px", borderBottom: `1px solid ${P.bd}` }}>
                          <span style={{ padding: "3px 10px", borderRadius: 8, background: h.status === "published" ? "#ECFDF5" : "#FFFBEB", color: h.status === "published" ? P.ok : "#D97706", fontSize: 12, fontWeight: 600 }}>{h.status === "published" ? "✅ منشور" : "📝 مسودة"}</span>
                        </td>
                        <td style={{ padding: "12px 14px", borderBottom: `1px solid ${P.bd}` }}>
                          <button onClick={() => setEditingHymn({ ...h, verses: h.verses?.map((v) => ({ ...v })) || [] })} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${P.pr}30`, background: "#E0F2FC", color: P.pr, fontSize: 13, cursor: "pointer", fontFamily: "inherit", marginInlineEnd: 6 }}>✎</button>
                          <button onClick={() => removeHymn(h.id)} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${P.dn}30`, background: "#FEF2F2", color: P.dn, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>🗑</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length === 0 && <div style={{ textAlign: "center", padding: 40, color: P.tx2 }}>لا توجد نتائج</div>}
            </div>
          </div>
        )}

        {/* HYMN EDITOR */}
        {section === "hymns" && editingHymn && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: P.pr }}>{editingHymn._isNew ? "➕ إضافة تمجيد جديد" : `✏️ تعديل: ${editingHymn.titleAr}`}</h2>
              <button onClick={() => setEditingHymn(null)} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${P.bd}`, background: P.s1, color: P.tx2, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← رجوع</button>
            </div>

            <div style={{ background: P.s1, borderRadius: 16, border: `1px solid ${P.bd}`, padding: 24, marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: P.pr, marginTop: 0, marginBottom: 16 }}>📋 البيانات الأساسية</h3>
              <div style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 14, marginBottom: 14 }}>
                <div><label style={lbl}>#</label><input style={inp} type="number" value={editingHymn.serialNumber} onChange={(e) => setEditingHymn({ ...editingHymn, serialNumber: +e.target.value })} /></div>
                <div><label style={lbl}>الفئة</label><select style={inp} value={editingHymn.categoryId} onChange={(e) => setEditingHymn({ ...editingHymn, categoryId: e.target.value })}>{sortedCats.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.nameAr}</option>)}</select></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                <div><label style={lbl}>العنوان بالعربي *</label><input style={inp} value={editingHymn.titleAr} onChange={(e) => setEditingHymn({ ...editingHymn, titleAr: e.target.value })} /></div>
                <div><label style={lbl}>Title (English)</label><input style={inpEn} value={editingHymn.titleEn} onChange={(e) => setEditingHymn({ ...editingHymn, titleEn: e.target.value })} /></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                <div><label style={lbl}>🎵 الوزن (عربي)</label><input style={inp} value={editingHymn.melodyAr} onChange={(e) => setEditingHymn({ ...editingHymn, melodyAr: e.target.value })} /></div>
                <div><label style={lbl}>🎵 Melody</label><input style={inpEn} value={editingHymn.melodyEn} onChange={(e) => setEditingHymn({ ...editingHymn, melodyEn: e.target.value })} /></div>
              </div>
              {isLiturgicalHymn && (
                <div style={{ marginBottom: 14 }}>
                  <div><label style={lbl}>🎶 اسم اللحن (لقسم المقدمة/الختام)</label><input style={inp} value={editingHymn.tuneName || ""} onChange={(e) => setEditingHymn({ ...editingHymn, tuneName: e.target.value })} placeholder="يظهر في منتصف صفحة التمجيد بخط كبير" /></div>
                </div>
              )}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                <div><label style={lbl}>📅 المناسبة</label><input style={inp} value={editingHymn.occasionAr} onChange={(e) => setEditingHymn({ ...editingHymn, occasionAr: e.target.value })} /></div>
                <div><label style={lbl}>📅 Occasion</label><input style={inpEn} value={editingHymn.occasionEn} onChange={(e) => setEditingHymn({ ...editingHymn, occasionEn: e.target.value })} /></div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>أيقونة التمجيد {isImg(editingHymn.icon) ? "— 🖼️ صورة" : (editingHymn.icon ? `— ${editingHymn.icon}` : "")}</label>
                {/* معاينة الأيقونة + اختيار صورة من الجهاز (تُحفظ داخل قاعدة البيانات مثل صورة القسم) */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 56, height: 56, borderRadius: "50%", overflow: "hidden", border: `2px solid ${P.bd}`, display: "flex", alignItems: "center", justifyContent: "center", background: P.bg, flexShrink: 0 }}>
                    {isImg(editingHymn.icon)
                      ? <img src={editingHymn.icon} alt="icon" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      : <span style={{ fontSize: 28 }}>{editingHymn.icon || "🎵"}</span>}
                  </div>
                  <label style={{ padding: "10px 14px", borderRadius: 10, background: P.pr, color: "#fff", fontSize: 13, cursor: "pointer", fontFamily: "inherit", fontWeight: 600 }}>
                    🖼️ اختيار صورة
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleHymnIconFile} />
                  </label>
                  {isImg(editingHymn.icon) && (
                    <button onClick={() => setEditingHymn({ ...editingHymn, icon: "🎵" })} style={{ padding: "10px 14px", borderRadius: 10, background: "#FEF2F2", color: P.dn, border: `1px solid ${P.dn}30`, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>حذف الصورة</button>
                  )}
                </div>
                <div style={{ fontSize: 12, color: P.tx2, marginBottom: 6 }}>أو اختر رمزًا:</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", maxHeight: 110, overflowY: "auto", padding: 6, border: `1px solid ${P.bd}`, borderRadius: 10, background: P.bg }}>
                  {ICON_SET.map((ic) => (
                    <button key={ic} onClick={() => setEditingHymn({ ...editingHymn, icon: ic })} style={{ width: 38, height: 38, fontSize: 21, borderRadius: 8, cursor: "pointer", background: (!isImg(editingHymn.icon) && editingHymn.icon === ic) ? `${P.pr}22` : "#fff", border: `2px solid ${(!isImg(editingHymn.icon) && editingHymn.icon === ic) ? P.pr : P.bd}` }}>{ic}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div><label style={lbl}>🔗 يوتيوب</label><input style={inpEn} value={editingHymn.youtubeUrl} onChange={(e) => setEditingHymn({ ...editingHymn, youtubeUrl: e.target.value })} placeholder="https://youtube.com/watch?v=..." /></div>
                <div style={{ display: "flex", gap: 14, alignItems: "end" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}><input type="checkbox" checked={editingHymn.isFeatured} onChange={(e) => setEditingHymn({ ...editingHymn, isFeatured: e.target.checked })} style={{ width: 18, height: 18, accentColor: P.ac }} /><span style={{ fontSize: 14 }}>⭐ مميز</span></label>
                  <select style={{ ...inp, width: 140 }} value={editingHymn.status} onChange={(e) => setEditingHymn({ ...editingHymn, status: e.target.value })}><option value="draft">📝 مسودة</option><option value="published">✅ منشور</option></select>
                </div>
              </div>
            </div>

            {/* Verses */}
            <div style={{ background: P.s1, borderRadius: 16, border: `1px solid ${P.bd}`, padding: 24, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: P.pr, margin: 0 }}>📝 الأبيات ({editingHymn.verses.length})</h3>
                <button onClick={addVerse} style={{ padding: "6px 16px", borderRadius: 8, background: "#ECFDF5", color: P.ok, border: "none", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>+ بيت جديد</button>
              </div>
              {editingHymn.verses.map((v, i) => (
                <div key={i} style={{ background: P.bg, borderRadius: 12, border: `1px solid ${P.bd}`, marginBottom: 12, overflow: "hidden" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 14px", borderBottom: `1px solid ${P.bd}`, background: P.s1 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: P.pr }}>بيت {v.verseNumber}</span>
                    <button onClick={() => removeVerse(i)} disabled={editingHymn.verses.length <= 1} style={{ background: "none", border: "none", color: P.dn, cursor: "pointer", fontSize: 14, opacity: editingHymn.verses.length <= 1 ? 0.3 : 1 }}>✕</button>
                  </div>
                  <div style={{ padding: 14 }}>
                    {[
                      ...(isLiturgicalHymn ? [
                        { k: "coptic", label: "قبطي (Coptic)", en: true, single: true },
                        { k: "copticArabic", label: "قبطي معرّب (بالعربي)", en: false, single: true },
                      ] : []),
                      { r: "arRight", l: "arLeft", label: "عربي", en: false },
                      { r: "enRight", l: "enLeft", label: "English", en: true },
                    ].map((row) => (
                      <div key={row.k || row.r} style={{ marginBottom: 10 }}>
                        {row.single ? (
                          <>
                            <label style={lbl}>{row.label}</label>
                            <textarea placeholder={row.label} style={{ ...(row.en ? inpEn : inp), width: "100%", minHeight: 46, resize: "vertical", lineHeight: 1.8, boxSizing: "border-box" }} value={v[row.k] || ""} onChange={(e) => updateVerse(i, row.k, e.target.value)} />
                          </>
                        ) : (
                          <>
                            <label style={lbl}>{row.label} — يمين / يسار</label>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                              <textarea placeholder="الشطر الأيمن" style={{ ...(row.en ? inpEn : inp), minHeight: 46, resize: "vertical", lineHeight: 1.8 }} value={v[row.r] || ""} onChange={(e) => updateVerse(i, row.r, e.target.value)} />
                              <textarea placeholder="الشطر الأيسر" style={{ ...(row.en ? inpEn : inp), minHeight: 46, resize: "vertical", lineHeight: 1.8 }} value={v[row.l] || ""} onChange={(e) => updateVerse(i, row.l, e.target.value)} />
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={saveHymn} style={{ flex: 1, padding: 14, borderRadius: 14, background: `linear-gradient(135deg, #0B5FA5, ${P.pr})`, color: "#fff", border: "none", fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>💾 حفظ</button>
              <button onClick={() => setEditingHymn(null)} style={{ flex: 1, padding: 14, borderRadius: 14, background: "transparent", color: P.tx2, border: `1.5px solid ${P.bd}`, fontSize: 16, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
              {!editingHymn._isNew && <button onClick={() => removeHymn(editingHymn.id)} style={{ padding: "14px 20px", borderRadius: 14, background: "#FEF2F2", color: P.dn, border: `1px solid ${P.dn}30`, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>🗑 حذف</button>}
            </div>
          </div>
        )}

        {/* CATEGORIES SECTION */}
        {section === "cats" && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: P.pr }}>📂 إدارة الأقسام ({cats.length})</h2>
              <button onClick={openNewCat} style={{ padding: "10px 22px", borderRadius: 12, background: `linear-gradient(135deg, ${P.ac}, #E8882D)`, color: "#fff", border: "none", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>+ قسم جديد</button>
            </div>
            <div style={{ background: P.s1, borderRadius: 16, border: `1px solid ${P.bd}` }}>
              {(() => {
                const byParent = {};
                cats.forEach((c) => { const p = c.parentId || ""; (byParent[p] = byParent[p] || []).push(c); });
                Object.values(byParent).forEach((a) => a.sort((x, y) => (x.order || 0) - (y.order || 0)));
                const flat = [];
                const walk = (pid, depth, guard) => {
                  if (guard > 6) return;
                  (byParent[pid] || []).forEach((c) => { flat.push({ c, depth }); walk(c.id, depth + 1, guard + 1); });
                };
                walk("", 0, 0);
                // الأقسام اليتيمة (أبٌ غير موجود) تُعرض كجذور حتى لا تختفي
                const shown = new Set(flat.map((f) => f.c.id));
                cats.forEach((c) => { if (!shown.has(c.id)) flat.push({ c, depth: 0 }); });
                return flat.map(({ c, depth }, i) => (
                  <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", paddingRight: 20 + depth * 30, borderBottom: i < flat.length - 1 ? `1px solid ${P.bd}` : "none", background: depth > 0 ? "#F6FBFE" : "transparent" }}>
                    {depth > 0 && <span style={{ color: P.tx2, fontSize: 15, opacity: 0.7 }}>└─</span>}
                    <div style={{ width: depth > 0 ? 40 : 50, height: depth > 0 ? 40 : 50, borderRadius: 12, background: `${c.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: depth > 0 ? 22 : 26, border: `2px solid ${c.color}30`, flexShrink: 0, overflow: "hidden" }}>{catIconNode(c, depth > 0 ? 22 : 26)}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: depth > 0 ? 15 : 16, fontWeight: 700 }}>{c.nameAr}</div>
                      <div style={{ fontSize: 13, color: P.tx2, fontFamily: "'Outfit'" }}>{c.nameEn}</div>
                      <span style={{ fontSize: 11, padding: "2px 10px", borderRadius: 6, background: `${c.color}12`, color: c.color, fontWeight: 600 }}>{hymns.filter((h) => h.categoryId === c.id).length} تمجيد</span>
                    </div>
                    <button onClick={() => setEditingCat({ ...c })} style={{ padding: "8px 16px", borderRadius: 10, border: `1.5px solid ${P.pr}30`, background: "#E0F2FC", color: P.pr, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>✎ تعديل</button>
                    <button onClick={() => removeCat(c.id)} style={{ padding: "8px 14px", borderRadius: 10, border: `1.5px solid ${P.dn}30`, background: "#FEF2F2", color: P.dn, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>🗑</button>
                  </div>
                ));
              })()}
            </div>
          </div>
        )}
      </div>

      {/* CATEGORY EDITOR MODAL */}
      {editingCat && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }} onClick={() => setEditingCat(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: P.s1, borderRadius: 20, width: "100%", maxWidth: 460, maxHeight: "85vh", overflow: "auto", direction: "rtl", padding: 24 }}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: P.pr, marginTop: 0 }}>{editingCat._isNew ? "➕ قسم جديد" : "✏️ تعديل القسم"}</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
              <div><label style={lbl}>الاسم بالعربي *</label><input style={inp} value={editingCat.nameAr} onChange={(e) => setEditingCat({ ...editingCat, nameAr: e.target.value })} /></div>
              <div><label style={lbl}>Name (English) *</label><input style={inpEn} value={editingCat.nameEn} onChange={(e) => setEditingCat({ ...editingCat, nameEn: e.target.value })} /></div>
            </div>
            {editingCat._isNew && <div style={{ marginBottom: 16 }}><label style={lbl}>المعرّف (ID)</label><input style={inpEn} value={editingCat.id} onChange={(e) => setEditingCat({ ...editingCat, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} placeholder="auto_generated" /></div>}
            <div style={{ marginBottom: 16 }}><label style={lbl}>الترتيب (رقم — الأصغر يظهر أولًا)</label><input style={inp} type="number" value={editingCat.order ?? 0} onChange={(e) => setEditingCat({ ...editingCat, order: +e.target.value })} /></div>
            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>القسم الأب (اتركه فارغًا لقسم رئيسي)</label>
              <select style={inp} value={editingCat.parentId || ""} onChange={(e) => setEditingCat({ ...editingCat, parentId: e.target.value })}>
                <option value="">— قسم رئيسي (بدون أب) —</option>
                {cats.filter((c) => c.id !== editingCat.id).map((c) => (
                  <option key={c.id} value={c.id}>{c.nameAr}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>الأيقونة (صورة من جهازك)</label>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 64, height: 64, borderRadius: 14, background: `${editingCat.color}15`, border: `2px solid ${editingCat.color}30`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                  {isImg(editingCat.image)
                    ? <img src={editingCat.image} alt="icon" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                    : <span style={{ fontSize: 30 }}>{editingCat.icon || "☩"}</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "inline-block", padding: "10px 18px", borderRadius: 10, background: "#E0F2FC", color: P.pr, fontSize: 14, fontWeight: 600, cursor: "pointer", border: `1.5px solid ${P.pr}30` }}>
                    📎 اختر صورة
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleIconFile} />
                  </label>
                  {isImg(editingCat.image) && (
                    <button onClick={() => setEditingCat({ ...editingCat, image: "" })} style={{ marginInlineStart: 8, padding: "10px 14px", borderRadius: 10, background: "#FEF2F2", color: P.dn, border: `1px solid ${P.dn}30`, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>حذف الصورة</button>
                  )}
                  <div style={{ fontSize: 11, color: P.tx2, marginTop: 6 }}>PNG أو JPG — تُصغَّر تلقائيًا وتُحفظ مع القسم</div>
                </div>
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>أو اختر رمزًا جاهزًا (بدون صورة)</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", maxHeight: 130, overflowY: "auto", padding: 6, border: `1px solid ${P.bd}`, borderRadius: 10, background: P.bg }}>
                {ICON_SET.map((ic) => (
                  <button key={ic} onClick={() => setEditingCat({ ...editingCat, icon: ic, image: "", iconType: "emoji" })} style={{ width: 40, height: 40, fontSize: 22, borderRadius: 8, cursor: "pointer", background: (!isImg(editingCat.image) && editingCat.icon === ic) ? `${editingCat.color}22` : "#fff", border: `2px solid ${(!isImg(editingCat.image) && editingCat.icon === ic) ? editingCat.color : P.bd}` }}>{ic}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}><label style={lbl}>اللون</label><div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {COLORS.map((c) => <button key={c} onClick={() => setEditingCat({ ...editingCat, color: c })} style={{ width: 30, height: 30, borderRadius: 8, background: c, border: `3px solid ${editingCat.color === c ? "#fff" : "transparent"}`, boxShadow: editingCat.color === c ? `0 0 0 2px ${c}` : "none", cursor: "pointer" }} />)}
              <input type="color" value={editingCat.color} onChange={(e) => setEditingCat({ ...editingCat, color: e.target.value })} style={{ width: 30, height: 30, border: "none", borderRadius: 8, cursor: "pointer" }} />
            </div></div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={saveCat} style={{ flex: 1, padding: 14, borderRadius: 14, background: `linear-gradient(135deg, #0B5FA5, ${P.pr})`, color: "#fff", border: "none", fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>💾 حفظ</button>
              <button onClick={() => setEditingCat(null)} style={{ flex: 1, padding: 14, borderRadius: 14, background: "transparent", color: P.tx2, border: `1.5px solid ${P.bd}`, fontSize: 16, cursor: "pointer", fontFamily: "inherit" }}>إلغاء</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: P.tx, color: "#fff", padding: "12px 28px", borderRadius: 50, fontSize: 14, fontWeight: 600, zIndex: 200, fontFamily: "inherit" }}>{toast}</div>}
    </div>
  );
}
